import { Search as SearchIcon } from '@mui/icons-material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Box, Container, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import React, {useEffect, useRef, useState } from 'react';
import { theme } from '../../../../themes';
import { Props } from '../container';
import { useDispatch, useSelector} from 'react-redux';
import { WO2TextInput } from './../../../../common';
import { TermDepositTable } from './TermDepositTable';
import { downloadTermDeposits, fetchExpiringTermDeposit } from '../../store/thunks';
import { fetchClientList } from 'src/features/clients/list/store/thunks';
import DatePicker from '@mui/lab/DatePicker';
import TextField from '@mui/material/TextField';
import { TermDepositRequest } from '../../types';
import WO2Button from 'src/common/components/button/Button';
import { sanitizeSearch } from 'src/common/utils/termDepositsFormatters';
import { ClientItem, fetchPagedResults } from 'src/features/clients/list/store/types';
import { RootState } from 'src/store/reducer';

export const TermDepositList = (props: Props): JSX.Element => {

  const dispatch = useDispatch();
  const { parameters, termDepsoitParameters, loadingProgress, setSearchStringParameter} = props;
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [clientIds, setClientIds] = useState<number[]>([]);
  const isFirstLoad = useRef(true);

       useEffect(() => {
            (dispatch(fetchClientList({
                inputs: parameters,
                pagination: parameters.pagination,
                isDataFromTriumph: true
            })) as unknown as Promise<{ payload: fetchPagedResults<ClientItem> }>).then((action) => {

                const ids = action.payload?.results?.results?.map((c: ClientItem) => c.clientId).filter((id) => id > 0) ?? [];
                setClientIds(ids);

                if(ids.length === 0) return;

                loadTermDeposits({ 
                    clientIds: ids, 
                    asAtDate: selectedDate ?? undefined, 
                    pagination: termDepsoitParameters.pagination 
                });  
            });
        }, [dispatch, parameters]);

        useEffect(() => {
            if (clientIds.length === 0) return;
           
            if (isFirstLoad.current) {
                    isFirstLoad.current = false;
                    return;
            }

        loadTermDeposits({ clientIds: clientIds, 
            asAtDate: selectedDate ?? undefined, 
            pagination: termDepsoitParameters.pagination });
    
     }, [selectedDate, clientIds]);

    const termDepositList = useSelector(
        (state: RootState) => state.expiringTermDeposit?.list?.termDepositList
    );

    const [selectedTab, setValue] = React.useState(2);
    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };
  
    const loadTermDeposits = (pagedRequest: TermDepositRequest) => {
        dispatch(fetchExpiringTermDeposit({
            clientIds: pagedRequest.clientIds,
            AsAtDate: pagedRequest.asAtDate ?? undefined,
            clientNameSearch: termDepsoitParameters.clientNameSearch,
            PagedRequest: pagedRequest.pagination,
            queryFields: [
            {
                fieldName: 'name',
                descendingSortDirection: false,
                operator: '',
                hasSearchTerm: false,
                isSortTerm: true,
                searchTerm: '',
            }],
        }));
    };

    const handleSearchClick = () => {
        loadTermDeposits({ 
            clientIds: clientIds, 
            asAtDate: selectedDate ?? undefined, 
            pagination: termDepsoitParameters.pagination 
        });
    };

    return (
        <Container style={{ paddingTop: '20px', paddingBottom: '20px' }}>
        <Tabs
            value={selectedTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleTabChange}
            style={{ paddingBottom: '30px' }}
            TabIndicatorProps={{ style: { height: '4px' } }}    
        >
            <Tab label="Securities" href="/security/list" />
            <Tab label="Forex Rates" href="/forexrate/list" />
            <Tab label="Expiring Term Deposits" href="/termDeposit/list" />
        </Tabs>
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                width: '100%',
                marginBottom: '5px'
            }}>
                <Grid item xs={12} sm={6}>
                    <Typography
                        sx={{
                            ml: { xs: '0px', sm: '10px', md: '0px' },
                            mb: { xs: '10px', sm: '60px', md: '0px' },
                            height: '48px',
                            color: '#000a12',
                            fontFamily: theme.typography.fontFamily,
                            fontSize: '36px',
                            fontWeight: 400,
                            lineHeight: '48px',       
                        }}>
                        Expiring Term Deposits
                    </Typography>
                </Grid>
                <Box display={'flex'} justifyContent={'space-between'} style={{ padding: '10px 0' }}>
                    <Box display={'flex'} justifyContent={'space-between'}>
                        <Box 
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                paddingRight: 0,
                            }}>
                          <DatePicker
                                label="Date"
                                value={selectedDate}
                                onChange={(newValue) => setSelectedDate(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (<>
                                            {selectedDate && (
                                                <button onClick={() => setSelectedDate(null)}>clear</button>
                                            )}
                                            {params.InputProps?.endAdornment}
                                        </>
                                        )
                                        }}
                                    />
                                )}
                            />
                            <WO2TextInput
                                placeholder={'Term Deposit Search'}
                                inputProps={{ 'aria-label': 'search' }}
                                onChange={(event) => {
                                    setSearchStringParameter(sanitizeSearch(event.target.value));
                                }}
                                InputProps={{ startAdornment: <SearchIcon style={{ margin: '0 10px' }}>search</SearchIcon> }}
                                value={props.termDepsoitParameters.clientNameSearch}
                            />
                            <WO2Button
                                color="primary"
                                disableElevation
                                style={{ fontFamily: theme.typography.fontFamily }}
                                onClick={handleSearchClick}
                                variant="contained"
                                value="Search"
                            >
                                Search
                            </WO2Button>  
                        </Box>
                    </Box>
                </Box>
        </Box>
        <Grid 
          sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                paddingRight: 0,
                ml: { xs: '0px', sm: '0px' },  
           }}>
            <Grid item xs={12}>
                <TermDepositTable
                    pagedResults={termDepositList ?? undefined}
                    pagedRequest={termDepsoitParameters.pagination}
                    progress={loadingProgress}
                    handlePagedRequest={(newPage) =>
                        loadTermDeposits({
                            clientIds,
                            asAtDate: selectedDate ?? undefined,
                            pagination: newPage
                        })
                    }
                    handleDownload={(pagedRequest, mapper) => {
                         dispatch(
                            downloadTermDeposits({
                                clientIds,
                                asAtDate: selectedDate ?? undefined,
                                pagination: pagedRequest,
                                clientNameSearch: termDepsoitParameters.clientNameSearch,
                                queryFields: termDepsoitParameters.pagination.queryFields,
                                mapper,
                            })
                        )
                    }}
                />
            </Grid>
        </Grid>
        </Container>
    );
}
